// Channel list store. Loads + filters + groups all live channels across the
// user's enabled sources. Backs the sidebar (Phase 2.5) AND the
// `<FavoriteChannelsStrip>` on Home (Phase 2.3 placeholder is replaced by
// this store) AND the `<RecordingsPage>` channel picker for M3U sources
// (Phase 2.3 only handled Xtream; this store unifies M3U + Xtream loads).
//
// State shape mirrors the legacy `ChannelList.js` constructor (lines 7-46)
// with two `[NEW]` improvements per the 2026-04-29 contract reconciliation:
//
//   - **`_fetchSeq` per source race guard.** Legacy has none — concurrent
//     multi-source loads can race and overwrite each other. The store
//     stamps each load with an incrementing seq and discards stale results.
//   - **Keyboard `selectPrev` / `selectNext` skip collapsed-group channels.**
//     Legacy (ChannelList.js:1966-2048) iterates `renderedChannels` which
//     includes collapsed-group channels — the user hits Down arrow expecting
//     to skip the collapsed group and instead lands "inside" the invisible
//     content. The store's `visibleChannels` getter excludes collapsed
//     groups so the keyboard nav stays in sync with the visible list.

import { defineStore } from 'pinia';
import { ref, shallowRef, computed, markRaw, type ComputedRef, type ShallowRef } from 'vue';
import { api } from '@/lib/api';
import { log } from '@/lib/log';
import { useFiltersStore } from './filters';
import { useFavoritesStore } from './favorites';
import {
    rankLiveSearch,
    parseLiveSearch,
    matchesLiveSearch,
    observedSearchQuality,
    shouldAutoCollapse,
    channelRegionKey,
} from '@/lib/liveSearch';
import { parseTags, KNOWN_CHANNEL_TAGS, isSeparatorChannelName } from '@/lib/channelTags';
import { getObservationSignal } from '@/composables/useObservationSignals';

export interface Channel {
    sourceId: number;
    /** Stream ID (Xtream `stream_id`, M3U row id). Stable across reloads. */
    id: string;
    /** Provider channel number (Xtream `num`) when supplied. */
    channelNumber?: number | null;
    /** Display name — already merged with `customName` when present. */
    name: string;
    /** Original upstream name (pre-override) for grep / debug. */
    rawName: string;
    /** Group / category. After custom-group merge. */
    groupTitle: string;
    /** Original provider group title. Used when "Use my group layout" is off. */
    providerGroupTitle?: string;
    /** EPG language tag (`en`, `de`, …) when known. */
    language: string | null;
    /** Logo URL — already merged with `customLogoUrl` when present. May be `''` (sentinel). */
    streamIcon: string;
    /**
     * The per-user logo OVERRIDE, unmerged — `null`/absent when the channel is
     * showing the provider's own icon.
     *
     * Carried separately from `streamIcon` because "is there an override" is
     * not recoverable from the merged value: once an override is set,
     * `streamIcon` IS the override and looks identical to a provider icon.
     * <LogoPicker> needs the distinction for its "Use the provider's logo"
     * button and to prefill the URL field.
     */
    override_custom_logo_url?: string | null;
    /** XMLTV channel id used to look up programmes. */
    epgChannelId: string | null;
    /** Catchup archive depth in hours. Drives play-from-archive eligibility. */
    archiveHours: number;
    /** Raw upstream stream URL (M3U sources). Empty for Xtream (resolved via API at play time). */
    streamUrl: string;
    /** Effective custom/provider category_id used by custom-layout reorder actions. */
    categoryId: string | null;
    /** Original provider category_id. Used for provider-layout group actions. */
    providerCategoryId?: string | null;
}

interface RawXtreamLiveStream {
    stream_id?: number | string;
    num?: number | string | null;
    name?: string;
    epg_channel_id?: string | null;
    stream_icon?: string | null;
    language?: string | null;
    category_id?: string | null;
    upstream_category_id?: string | null;
    stream_url?: string | null;
    tv_archive?: number;
    tv_archive_duration?: number;
    override_custom_name?: string | null;
    override_custom_logo_url?: string | null;
    override_custom_group_name?: string | null;
    override_custom_group_id?: string | null;
}

interface RawXtreamLiveCategory {
    category_id?: string | number;
    category_name?: string;
    is_custom_group?: number | boolean;
}

function normaliseXtream(
    sourceId: number,
    streams: RawXtreamLiveStream[],
    categoryMap: Map<string, { name: string; custom: boolean }>,
): Channel[] {
    const out: Channel[] = [];
    for (const s of streams) {
        const id = String(s.stream_id ?? '');
        if (!id) continue;
        const rawName = String(s.name ?? '');
        const overrideName = s.override_custom_name?.trim();
        const name = overrideName && overrideName.length > 0 ? overrideName : rawName;
        const effectiveCategoryId = s.category_id != null ? String(s.category_id) : null;
        const providerCategoryId = s.upstream_category_id != null
            ? String(s.upstream_category_id)
            : effectiveCategoryId;
        const effectiveCategory = effectiveCategoryId ? categoryMap.get(effectiveCategoryId) : undefined;
        const providerCategory = providerCategoryId ? categoryMap.get(providerCategoryId) : undefined;
        const groupOverride = s.override_custom_group_name?.trim();
        const rawGroupTitle = groupOverride && groupOverride.length > 0
            ? groupOverride
            : (effectiveCategory?.name ?? '');
        // The proxy marks local categories with a leading star for legacy
        // clients. Atlas has an explicit "local" badge, so keep the model name
        // canonical and decoration-free.
        const groupTitle = effectiveCategory?.custom
            ? rawGroupTitle.replace(/^★\s*/, '')
            : rawGroupTitle;
        const providerGroupTitle = providerCategory?.name ?? groupTitle;
        const overrideLogo = s.override_custom_logo_url?.trim();
        const streamIcon = overrideLogo && overrideLogo.length > 0 ? overrideLogo : (s.stream_icon ?? '');
        const rawChannelNumber = Number(s.num);
        const channelNumber = Number.isFinite(rawChannelNumber) && rawChannelNumber > 0
            ? Math.trunc(rawChannelNumber)
            : null;
        // markRaw — defense in depth against accidental future Proxy
        // wrapping if a caller spreads a channel into a `reactive()`
        // parent. shallowRef on the array already prevents per-item
        // proxying, but `markRaw` is the doc-recommended belt-and-braces
        // for "large arrays of immutable items"
        // (vuejs.org/api/reactivity-advanced#markraw).
        out.push(markRaw({
            sourceId,
            id,
            channelNumber,
            name,
            rawName,
            groupTitle,
            providerGroupTitle,
            categoryId: effectiveCategoryId,
            providerCategoryId,
            language: s.language ?? null,
            streamIcon,
            override_custom_logo_url: s.override_custom_logo_url ?? null,
            epgChannelId: s.epg_channel_id ?? null,
            archiveHours: Number(s.tv_archive_duration ?? 0) || 0,
            streamUrl: s.stream_url ?? '',
        }));
    }
    return out;
}

export const useChannelListStore = defineStore('channelList', () => {
    // shallowRef — Vue tracks reassignment of the array reference but does
    // not Proxy each individual Channel object. Multi-source Xtream
    // accounts can hit 10k+ channels; deep reactivity over that many
    // objects allocates hundreds of MB of Proxy state and slows every
    // computed (`groupedChannels`, `filteredChannels`, `flatRows` in the
    // sidebar) that walks the list. We only ever bulk-replace the array,
    // so shallow tracking is sufficient.
    const channels: ShallowRef<Channel[]> = shallowRef([]);
    const sources = ref<Array<{ id: number; name: string; type: string; enabled?: boolean }>>([]);
    const selectedSourceId = ref<number | null>(null);
    const searchTerm = ref<string>('');
    const isLoading = ref<boolean>(false);
    const currentChannel = ref<Channel | null>(null);
    // Per-user custom group display order (stable category keys, ASC). Applied in
    // `groupedChannels` when `filters.data.useMyGroupLayout` is on. Loaded from
    // the server alongside channels; written via `setGroupOrder` (Manage reorder).
    const groupOrder = ref<string[]>([]);
    const groupOrderLegacy = ref(false);
    const groupLayout = ref<Record<string, { customName: string | null; userHidden: boolean }>>({});

    /** Per-source incrementing fetch counter — `[NEW]` race guard vs legacy. */
    const fetchSeq = ref<Map<number, number>>(new Map());
    /** Outer-call seq — guards `channels.value =` against an older `loadChannels` finishing after a newer one. */
    let loadSeq = 0;

    const filters = useFiltersStore();
    const favorites = useFavoritesStore();

    /** Group identity active for the global Custom ↔ Provider layout mode. */
    function groupTitleFor(channel: Channel): string {
        if (filters.data.useMyGroupLayout === false) {
            return channel.providerGroupTitle || channel.groupTitle || 'Uncategorised';
        }
        const override = groupLayout.value[groupKeyFor(channel)];
        return override?.customName || channel.groupTitle || 'Uncategorised';
    }

    /** Category identity matching `groupTitleFor`, for group-level actions. */
    function categoryIdFor(channel: Channel): string | null {
        if (filters.data.useMyGroupLayout === false) {
            return channel.providerCategoryId ?? channel.categoryId;
        }
        return channel.categoryId;
    }

    /** Stable identity for the category currently active in the layout model. */
    function groupKeyFor(channel: Channel): string {
        const categoryId = categoryIdFor(channel);
        if (!categoryId) return `uncategorised:${channel.sourceId}`;
        if (categoryId.startsWith('usr:')) return categoryId;
        return `${channel.sourceId}:${categoryId}`;
    }

    function isGroupHidden(channel: Channel): boolean {
        if (filters.data.useMyGroupLayout === false) return false;
        return groupLayout.value[groupKeyFor(channel)]?.userHidden === true;
    }

    function groupOrderIndex(title: string, items: Channel[], orderIdx: Map<string, number>): number {
        if (groupOrderLegacy.value) return orderIdx.get(title) ?? Number.POSITIVE_INFINITY;
        let index = Number.POSITIVE_INFINITY;
        for (const channel of items) {
            index = Math.min(index, orderIdx.get(groupKeyFor(channel)) ?? Number.POSITIVE_INFINITY);
        }
        return index;
    }

    // ---- selectors / getters --------------------------------------------

    /**
     * Channels filtered by source + country/region(s) + tags + measured quality,
     * but NOT by `searchTerm`. Pre-collapse.
     * Atlas Phase 6 (spec §5.1): the LANGUAGE filter was REMOVED — it was a fuzzy
     * name-match that collided with region prefixes and produced false positives.
     * Region (from the reliable group-name prefix) is the kept facet.
     *
     * SCOPE CONTRACT (post-4.10 rule #3): `searchTerm` is the LIVE SIDEBAR's
     * state, not a global one — the Guide owns a separate search box with its
     * own term. Consumers that render their own search input MUST build from
     * this getter (or `groupedChannelsUnsearched`); only the Live sidebar's own
     * surfaces may use the search-applied `filteredChannels` below. Deriving the
     * Guide's tree from the search-applied getter is what let a Live search for
     * "TF1" silently narrow the Guide with no way to clear it there.
     *
     * Also drops decorative provider "section divider" rows (`isSeparatorChannelName`,
     * e.g. `##### UNITED KINGDOM #####`) — every consumer of this getter
     * (Live sidebar, Guide's grouped tree, Guide's keyboard-focus targets, both
     * search paths) renders a real `<button>` per channel that tunes the live
     * URL, and a divider is not a tunable stream. One filter here is the single
     * canonical fix for every layer rather than a per-renderer patch, per the
     * "one canonical resolver across every layer" rule — hiding it is safe: it
     * is one member of a much larger real category (measured: the "UNITED
     * KINGDOM" divider's own category carries 605 siblings), so the group
     * never goes empty from this alone, and its own group header already shows
     * the same label these rows exist to repeat.
     */
    const facetChannels: ComputedRef<Channel[]> = computed(() => {
        const countries = filters.data.channelCountryFilters;
        const sourceFilter = selectedSourceId.value;
        const countrySet = countries.length > 0 ? new Set(countries) : null;
        // Atlas R3 — TAGS facet (provider-claimed, §2.1). A channel matches if it
        // carries ANY selected tag. parseTags only runs when a tag filter is
        // active, so the 55k-channel hot path is unaffected when it's empty.
        const tags = filters.data.channelTagFilters;
        const tagSet = tags.length > 0 ? new Set(tags) : null;
        // MEASURED-quality facet — filters on the observation-signals cache
        // (probe / playback measurements), NOT provider claims. Channels with
        // no measurement are excluded while a minimum is set: the facet's
        // purpose is "show me measured-good streams" (the group bulk-probe is
        // how you widen coverage). Reading the reactive cache here re-runs
        // this computed as probe results land — matching rows appear live.
        const minHeight = filters.data.channelMinHeight;
        const minFps = filters.data.channelMinFps;
        const multiAudioOnly = filters.data.channelMultiAudioOnly === true;
        return channels.value.filter((c) => {
            if (isSeparatorChannelName(c.name)) return false;
            if (isGroupHidden(c)) return false;
            if (sourceFilter !== null && c.sourceId !== sourceFilter) return false;
            const groupTitle = groupTitleFor(c);
            if (countrySet && !countrySet.has(channelRegionKey(groupTitle, c.name) ?? '')) return false;
            if (tagSet && !parseTags(c.name).some((t) => tagSet.has(t))) return false;
            if (minHeight != null || minFps != null || multiAudioOnly) {
                const ls = getObservationSignal(c.sourceId, c.id)?.lastSeen;
                if (!ls) return false;
                if (minHeight != null && (ls.height ?? 0) < minHeight) return false;
                if (minFps != null && (ls.fps ?? 0) < minFps) return false;
                // 2+ tracks is what makes the audio menu worth opening; a
                // channel measured at exactly 1 is a real "no", and one never
                // measured (null) cannot answer, so both are excluded.
                if (multiAudioOnly && (ls.audioTracks ?? 0) < 2) return false;
            }
            return true;
        });
    });

    /** `facetChannels` narrowed by the Live sidebar's `searchTerm`. */
    const filteredChannels: ComputedRef<Channel[]> = computed(() => {
        const term = searchTerm.value.trim();
        if (!term) return facetChannels.value;
        // Canonical matcher (quoted-phrase semantics) — the SAME parse the
        // ranked searchResults getter applies, so this pre-filter can never
        // disagree with the ranking layer (post-4.10 rule #2).
        const parsedSearch = parseLiveSearch(term);
        return facetChannels.value.filter(
            (c) =>
                matchesLiveSearch(c.name, parsedSearch)
                || matchesLiveSearch(groupTitleFor(c), parsedSearch),
        );
    });

    /**
     * Atlas Phase 6 (spec §5.1) — GLOBAL live search is always flat: when a
     * search term is present, return one ranked hit list across ALL groups
     * (favourites first, exact > prefix > substring) so duplicate names across
     * groups are distinguishable by their group label in the row. Ranking lives
     * in the pure `rankLiveSearch` helper (spec §6 #4 — load-bearing).
     */
    const searchResults: ComputedRef<Channel[]> = computed(() => {
        const term = searchTerm.value.trim();
        if (!term) return [];
        return rankLiveSearch(filteredChannels.value, term, {
            isFavorite: (c) => favorites.has(c.sourceId, c.id, 'channel'),
            // Atlas R6 (#17) — observed working-quality ranks within a name-match
            // tier: reliability first, then MEASURED res/fps/bitrate (so same-name
            // variants order 1080p50 → 720p25). Reads the reactive signals cache —
            // a bulk quality check re-orders the hits live as probes land.
            // Unknown/never-tried channels contribute 0 (no boost, not a penalty).
            // Provider claims are NEVER an input.
            workingQuality: (c) => observedSearchQuality(getObservationSignal(c.sourceId, c.id)),
        });
    });

    /**
     * The set of region prefixes present across the loaded channel list, via
     * the canonical `channelRegionKey` resolver (group title → name fallback;
     * handles "DE| Sport", "UK: News", "USA - Sports"). The Guide facet uses
     * the same resolver, so both pages surface identical chips. Sorts canonical
     * pinned prefixes first (DE/UK/US/FR) then alphabetical. Re-runs only when
     * `channels` reassigns (shallowRef).
     */
    const countryPrefixes: ComputedRef<string[]> = computed(() => {
        const set = new Set<string>();
        for (const c of channels.value) {
            const p = channelRegionKey(groupTitleFor(c), c.name);
            if (p) set.add(p);
        }
        const PIN = ['DE', 'UK', 'US', 'FR'];
        const arr = Array.from(set);
        const pinned = PIN.filter((p) => set.has(p));
        const rest = arr
            .filter((p) => !PIN.includes(p))
            .sort((a, b) => a.localeCompare(b));
        return [...pinned, ...rest];
    });

    // Atlas R3 — the provider TAGS present across the loaded channels, in the
    // canonical KNOWN_CHANNEL_TAGS order. Seeds the Tags facet typeahead so it
    // only offers tags that actually appear. Recomputes when `channels` reassigns.
    const availableTags: ComputedRef<string[]> = computed(() => {
        const set = new Set<string>();
        for (const c of channels.value) for (const t of parseTags(c.name)) set.add(t);
        return KNOWN_CHANNEL_TAGS.filter((t) => set.has(t));
    });

    /** Full catalogue grouped in the active layout, without search/facet/source filtering. */
    const allGroupedChannels: ComputedRef<Record<string, Channel[]>> = computed(() => {
        const out: Record<string, Channel[]> = {};
        for (const channel of channels.value) {
            if (isGroupHidden(channel)) continue;
            const group = groupTitleFor(channel);
            if (!out[group]) out[group] = [];
            out[group].push(channel);
        }
        const useOrder = filters.data.useMyGroupLayout !== false && groupOrder.value.length > 0;
        const orderIdx = new Map<string, number>();
        if (useOrder) groupOrder.value.forEach((title, index) => orderIdx.set(title, index));
        const titles = Object.keys(out).sort((a, b) => {
            if (useOrder) {
                const ia = groupOrderIndex(a, out[a]!, orderIdx);
                const ib = groupOrderIndex(b, out[b]!, orderIdx);
                if (ia !== ib) return ia - ib;
            }
            return a.localeCompare(b);
        });
        return Object.fromEntries(titles.map((title) => [title, out[title]!]));
    });

    // (Atlas Phase 6) The `languageCodes` computed was removed with the
    // language filter — region (countryPrefixes) is the reliable facet.

    /**
     * `groupTitle → Channel[]`, sorted alphabetically with Favourites first.
     *
     * Vue 3.4 computed-stability pattern (`vuejs.org/guide/best-practices/performance#computed-stability`):
     * the getter receives the previous return value and we shallow-compare
     * the freshly built shape to it; when nothing changed (same group keys,
     * same channel arrays per group), we return `oldValue` so downstream
     * computeds (`flatRows`, `visibleChannels`) don't re-run. Without this,
     * every favorite toggle / recordings tick that touches `filteredChannels`
     * deps produced a new object identity even when the data was identical.
     */
    function buildGroupTree(
        list: Channel[],
        oldValue: Record<string, Channel[]> | undefined,
    ): Record<string, Channel[]> {
            const out: Record<string, Channel[]> = {};
            const favouritesGroup: Channel[] = [];
            for (const c of list) {
                if (favorites.has(c.sourceId, c.id, 'channel')) favouritesGroup.push(c);
                const g = groupTitleFor(c);
                if (!out[g]) out[g] = [];
                out[g].push(c);
            }
            const sortedGroups: Record<string, Channel[]> = {};
            if (favouritesGroup.length > 0) sortedGroups['Favourites'] = favouritesGroup;
            // Apply the user's custom group order when "Use my group layout" is on
            // (default). Titles present in `groupOrder` sort by their saved index;
            // any title not in the list (e.g. a newly-synced provider category)
            // falls back to alphabetical AFTER the ordered set. Favourites stays
            // pinned at the very top regardless.
            const useOrder = filters.data.useMyGroupLayout !== false && groupOrder.value.length > 0;
            const orderIdx = new Map<string, number>();
            if (useOrder) groupOrder.value.forEach((t, i) => orderIdx.set(t, i));
            const groupCmp = (a: string, b: string): number => {
                if (useOrder) {
                    const ia = groupOrderIndex(a, out[a]!, orderIdx);
                    const ib = groupOrderIndex(b, out[b]!, orderIdx);
                    if (ia !== ib) return ia - ib;
                }
                return a.localeCompare(b);
            };
            for (const g of Object.keys(out).sort(groupCmp)) {
                sortedGroups[g] = out[g]!;
            }
            // Stability check — same key list AND each group references the
            // same channel array (cheap, since we'd only build a new array
            // when the source channels reference changed upstream).
            if (oldValue) {
                const oldKeys = Object.keys(oldValue);
                const newKeys = Object.keys(sortedGroups);
                if (oldKeys.length === newKeys.length) {
                    let same = true;
                    for (let i = 0; i < newKeys.length; i++) {
                        const k = newKeys[i]!;
                        if (oldKeys[i] !== k) { same = false; break; }
                        const a = oldValue[k];
                        const b = sortedGroups[k]!;
                        if (!a || a.length !== b.length) { same = false; break; }
                        // Reference compare each channel — they're shallowRef
                        // items so identity is stable across recomputes.
                        for (let j = 0; j < b.length; j++) {
                            if (a[j] !== b[j]) { same = false; break; }
                        }
                        if (!same) break;
                    }
                    if (same) return oldValue;
                }
            }
            return sortedGroups;
    }

    const groupedChannels: ComputedRef<Record<string, Channel[]>> = computed(
        (oldValue: Record<string, Channel[]> | undefined) => buildGroupTree(filteredChannels.value, oldValue),
    );

    /**
     * The same tree WITHOUT the Live sidebar's `searchTerm` applied — the getter
     * every consumer with its own search box builds from (today: the Guide).
     * See the scope contract on `facetChannels`.
     */
    const groupedChannelsUnsearched: ComputedRef<Record<string, Channel[]>> = computed(
        (oldValue: Record<string, Channel[]> | undefined) => buildGroupTree(facetChannels.value, oldValue),
    );

    /** Visible channels for keyboard nav — excludes collapsed groups (`[NEW]`). */
    const visibleChannels: ComputedRef<Channel[]> = computed(() => {
        const collapsed = new Set(filters.data.collapsedGroups);
        const visible: Channel[] = [];
        for (const [g, items] of Object.entries(groupedChannels.value)) {
            if (collapsed.has(g)) continue;
            visible.push(...items);
        }
        return visible;
    });

    // ---- actions --------------------------------------------------------

    async function loadSources(): Promise<void> {
        const all = await api.sources.getAll();
        sources.value = all
            .filter((s) => s.enabled !== false)
            .map((s) => ({ id: s.id, name: s.name, type: s.type, enabled: s.enabled }));
        // Default to first source on first load.
        if (selectedSourceId.value === null && sources.value.length > 0) {
            selectedSourceId.value = sources.value[0]!.id;
        }
    }

    async function loadChannels(): Promise<void> {
        if (sources.value.length === 0) {
            await loadSources();
        }
        const mySeq = ++loadSeq;
        isLoading.value = true;
        const targets = sources.value.filter((s) => s.type === 'xtream'); // M3U/Stalker added incrementally
        const out: Channel[] = [];
        try {
            await Promise.all(
                targets.map(async (src) => {
                    const seq = (fetchSeq.value.get(src.id) ?? 0) + 1;
                    fetchSeq.value.set(src.id, seq);
                    try {
                        const [cats, streams] = await Promise.all([
                            api.proxy.xtream.liveCategories(src.id),
                            api.proxy.xtream.liveStreams(src.id),
                        ]);
                        // Only commit results from the latest fetch — earlier
                        // races for the same source get discarded (`[NEW]`).
                        if (fetchSeq.value.get(src.id) !== seq) {
                            log.debug('channelList.stale_fetch_discarded', { sourceId: src.id, seq });
                            return;
                        }
                        const catMap = new Map<string, { name: string; custom: boolean }>();
                        for (const c of (cats as RawXtreamLiveCategory[]) ?? []) {
                            catMap.set(String(c.category_id ?? ''), {
                                name: String(c.category_name ?? ''),
                                custom: c.is_custom_group === true || c.is_custom_group === 1,
                            });
                        }
                        const items = normaliseXtream(src.id, (streams as RawXtreamLiveStream[]) ?? [], catMap);
                        out.push(...items);
                    } catch (err) {
                        log.warn('channelList.source_load_failed', {
                            sourceId: src.id,
                            error: (err as Error).message,
                        });
                    }
                }),
            );
            // Outer-call race guard — if a newer `loadChannels` started after
            // us, don't clobber its result with our stale buffer.
            if (mySeq !== loadSeq) {
                log.debug('channelList.stale_outer_load_discarded', { mySeq, currentSeq: loadSeq });
                return;
            }
            channels.value = out;
            // A resolved channel load must expose the user's final grouping,
            // not a provider-group snapshot that mutates a moment later. Guide
            // deep links depend on this boundary to expand and focus the
            // correct saved/custom group on a fresh page load.
            await Promise.all([loadGroupOrder(), loadGroupLayout()]);
            // Atlas Phase 6 (spec §5.1): on the very first load, auto-collapse
            // only LARGE groups (> ~20 channels); small groups stay OPEN so the
            // user sees content immediately. (Was: collapse-everything, legacy
            // parity.) Once the user toggles a group themselves the init flag
            // stays true and we never auto-collapse again.
            if (filters.data.collapsedGroupsInitialized === false) {
                const counts = new Map<string, number>();
                for (const c of out) {
                    const g = groupTitleFor(c);
                    if (g === 'Favourites') continue;
                    counts.set(g, (counts.get(g) ?? 0) + 1);
                }
                const large: string[] = [];
                for (const [g, n] of counts) if (shouldAutoCollapse(n)) large.push(g);
                filters.patch({
                    collapsedGroups: large,
                    collapsedGroupsInitialized: true,
                });
            }
            log.info('channelList.loaded', { count: out.length, sources: targets.length });
        } finally {
            // Clear loading only when we are the latest call, otherwise the
            // newer call already set it back true.
            if (mySeq === loadSeq) isLoading.value = false;
        }
    }

    // Group-order is fetched once (cheap GET); `setGroupOrder` keeps it fresh
    // optimistically thereafter, so we don't re-fetch on every channel reload.
    let groupOrderLoaded = false;
    async function loadGroupOrder(force = false): Promise<void> {
        if (groupOrderLoaded && !force) return;
        try {
            const { ordered, legacy } = await api.channels.me.groupOrder.get();
            groupOrder.value = Array.isArray(ordered) ? ordered : [];
            groupOrderLegacy.value = legacy === true;
            groupOrderLoaded = true;
        } catch (err) {
            log.warn('channelList.group_order_load_failed', { error: (err as Error).message });
        }
    }

    /**
     * Persist a new custom group display order (the full ordered list of stable
     * category keys). Optimistic: apply locally first so the sidebar + Manage reorder
     * instantly, then POST; roll back to the prior order on failure.
     */
    async function setGroupOrder(orderedKeys: string[]): Promise<void> {
        const before = groupOrder.value;
        const beforeLegacy = groupOrderLegacy.value;
        groupOrder.value = [...orderedKeys];
        groupOrderLegacy.value = false;
        groupOrderLoaded = true;
        try {
            await api.channels.me.groupOrder.set(orderedKeys);
            log.info('channelList.group_order_saved', { count: orderedKeys.length });
        } catch (err) {
            groupOrder.value = before;
            groupOrderLegacy.value = beforeLegacy;
            log.warn('channelList.group_order_save_failed', { error: (err as Error).message });
            throw err;
        }
    }

    let groupLayoutLoaded = false;
    async function loadGroupLayout(force = false): Promise<void> {
        if (groupLayoutLoaded && !force) return;
        try {
            const { groups } = await api.channels.me.groupLayout.get();
            groupLayout.value = Object.fromEntries(
                groups.map((row) => [
                    row.group_key,
                    {
                        customName: row.custom_name,
                        userHidden: row.user_hidden === 1,
                    },
                ]),
            );
            groupLayoutLoaded = true;
        } catch (err) {
            log.warn('channelList.group_layout_load_failed', { error: (err as Error).message });
        }
    }

    async function setGroupLayout(
        keys: string[],
        patch: { custom_name?: string | null; user_hidden?: boolean },
    ): Promise<void> {
        const before = groupLayout.value;
        const next = { ...before };
        for (const key of keys) {
            const current = next[key] ?? { customName: null, userHidden: false };
            const updated = {
                customName: patch.custom_name === undefined ? current.customName : patch.custom_name,
                userHidden: patch.user_hidden === undefined ? current.userHidden : patch.user_hidden,
            };
            if (updated.customName === null && !updated.userHidden) delete next[key];
            else next[key] = updated;
        }
        groupLayout.value = next;
        groupLayoutLoaded = true;
        try {
            await api.channels.me.groupLayout.update(keys, patch);
        } catch (err) {
            groupLayout.value = before;
            throw err;
        }
    }

    async function resetGroupLayout(): Promise<void> {
        const beforeLayout = groupLayout.value;
        const beforeOrder = groupOrder.value;
        const beforeLegacy = groupOrderLegacy.value;
        groupLayout.value = {};
        groupOrder.value = [];
        groupOrderLegacy.value = false;
        groupLayoutLoaded = true;
        groupOrderLoaded = true;
        try {
            await api.channels.me.groupLayout.reset();
        } catch (err) {
            groupLayout.value = beforeLayout;
            groupOrder.value = beforeOrder;
            groupOrderLegacy.value = beforeLegacy;
            throw err;
        }
    }

    function selectChannel(channel: Channel): void {
        currentChannel.value = channel;
    }

    function selectNext(): void {
        const list = visibleChannels.value;
        if (list.length === 0) return;
        const cur = currentChannel.value;
        if (!cur) {
            currentChannel.value = list[0]!;
            return;
        }
        const idx = list.findIndex((c) => c.sourceId === cur.sourceId && c.id === cur.id);
        const next = list[(idx + 1) % list.length] ?? list[0]!;
        currentChannel.value = next;
    }

    function selectPrev(): void {
        const list = visibleChannels.value;
        if (list.length === 0) return;
        const cur = currentChannel.value;
        if (!cur) {
            currentChannel.value = list[list.length - 1]!;
            return;
        }
        const idx = list.findIndex((c) => c.sourceId === cur.sourceId && c.id === cur.id);
        const prev = list[(idx - 1 + list.length) % list.length] ?? list[0]!;
        currentChannel.value = prev;
    }

    function setSearchTerm(term: string): void {
        searchTerm.value = term;
    }

    function setSourceId(id: number | null): void {
        selectedSourceId.value = id;
    }

    function toggleGroup(name: string): void {
        const next = new Set(filters.data.collapsedGroups);
        if (next.has(name)) next.delete(name);
        else next.add(name);
        filters.patch({ collapsedGroups: [...next] });
    }

    /**
     * Collapse or expand every currently-shown group at once.
     * When `collapsed` is true, patches `collapsedGroups` to every key in
     * `groupedChannels` (includes Favourites — literal "collapse all").
     * When `collapsed` is false, clears `collapsedGroups`.
     */
    function setAllCollapsed(collapsed: boolean): void {
        if (collapsed) {
            filters.patch({ collapsedGroups: Object.keys(groupedChannels.value) });
        } else {
            filters.patch({ collapsedGroups: [] });
        }
    }

    /**
     * True when there is ≥1 group AND every key in `groupedChannels` is in
     * `collapsedGroups`. False when no groups exist or ≥1 group is expanded.
     */
    const allGroupsCollapsed: ComputedRef<boolean> = computed(() => {
        const groups = Object.keys(groupedChannels.value);
        if (groups.length === 0) return false;
        const collapsed = new Set(filters.data.collapsedGroups);
        return groups.every((g) => collapsed.has(g));
    });

    function findChannel(sourceId: number, channelId: string): Channel | undefined {
        return channels.value.find((c) => c.sourceId === sourceId && c.id === channelId);
    }

    return {
        channels,
        sources,
        selectedSourceId,
        searchTerm,
        isLoading,
        currentChannel,
        groupOrder,
        groupOrderLegacy,
        groupLayout,
        facetChannels,
        filteredChannels,
        searchResults,
        groupedChannels,
        groupedChannelsUnsearched,
        visibleChannels,
        countryPrefixes,
        availableTags,
        allGroupedChannels,
        groupTitleFor,
        categoryIdFor,
        groupKeyFor,
        isGroupHidden,
        loadSources,
        loadChannels,
        loadGroupOrder,
        loadGroupLayout,
        setGroupOrder,
        setGroupLayout,
        resetGroupLayout,
        selectChannel,
        selectNext,
        selectPrev,
        setSearchTerm,
        setSourceId,
        toggleGroup,
        setAllCollapsed,
        allGroupsCollapsed,
        findChannel,
    };
});
